/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import Pagination from 'react-js-pagination';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q } from './kit';

const DEFAULT_LIMIT = 20;

/**
 * Generic inline paginated detail list scoped to one user + the shared date window.
 * Mirrors a standalone detail page's table so a box's drill-down can live inside the tab
 * instead of navigating away. The tab-level DateRangeBar drives `from`/`to` (the same
 * filter the detail page offers).
 *
 * Props:
 *   endpoint  — admin API path segment (e.g. 'bp-details'); called with page/limit/window.
 *   columns   — [{ label, render:(row, absIndex)=>node, className?, thClassName?, sortField? }].
 *               A column naming `sortField` becomes a clickable header; the server does the
 *               ordering, so the field must be one the endpoint whitelists.
 *   rowKey    — row → React key (default r.id).
 *   empty     — { title, message, icon } shown when the first page is empty.
 *   limit     — page size (default 20).
 *   defaultSortField / defaultSortOrder — initial ordering. Omit both and no sort params
 *               are sent at all, leaving the endpoint's own default untouched.
 *   onSortChange — ({sortField, sortOrder}) => void, fired when the reader re-sorts. Lets a
 *               sibling control (a CSV export, say) stay in step with what is on screen.
 */
const DetailList = ({
  userId, from, to, authGet, endpoint,
  columns, rowKey = (r) => r.id, testId,
  empty = {}, limit = DEFAULT_LIMIT,
  defaultSortField = '', defaultSortOrder = 'DESC', onSortChange,
}) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  useEffect(() => { setPage(1); }, [userId, from, to, endpoint]);

  // Same toggle rule as the standalone detail pages: a new column starts ASC, and
  // re-clicking the active one flips it. Sorting re-queries the server, so it must
  // return to page 1 — page 3 of the old order is a meaningless slice of the new one.
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortOrder(order);
    setSortField(field);
    setPage(1);
    // Fired from the click handler, not an effect — this is the only place sort moves,
    // and notifying during render would loop.
    if (onSortChange) onSortChange({ sortField: field, sortOrder: order });
  };

  const { status, data, reload } = useTabData(async () => {
    const sort = sortField ? { sortField, sortOrder } : {};
    const res = await authGet(`/admin/api/${endpoint}?${q(userId, from, to, { page, limit, ...sort })}`);
    return { rows: res.data.data || [], total: res.data.totalItem || 0 };
  }, [userId, from, to, endpoint, page, sortField, sortOrder]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const { rows, total } = data;
  if (!rows.length && page === 1) {
    return <TabEmpty title={empty.title || 'Nothing to show'} message={empty.message} icon={empty.icon} />;
  }

  return (
    <div className="au-panel" data-testid={testId}>
      <div className="au-table-wrap">
        <table className="au-table">
          <thead>
            <tr>
              {columns.map((c) => (c.sortField ? (
                <th
                  key={c.label}
                  className={`au-th-sort ${c.thClassName || ''}`.trim()}
                  aria-sort={sortField === c.sortField ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : 'none'}
                  onClick={() => handleSort(c.sortField)}
                >
                  {c.label} {sortField === c.sortField ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
                </th>
              ) : (
                <th key={c.label} className={c.thClassName}>{c.label}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={rowKey(r)}>
                {columns.map((c) => (
                  <td key={c.label} data-label={c.label} className={c.className}>
                    {c.render(r, (page - 1) * limit + i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > limit && (
        <div className="ud-pager">
          <Pagination activePage={page} itemsCountPerPage={limit} totalItemsCount={total} pageRangeDisplayed={5} onChange={setPage} />
        </div>
      )}
    </div>
  );
};

export default DetailList;
